import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create separate axios instances for different auth types
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ADMIN API instance
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for ADMIN token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for USER token (customer)
api.interceptors.request.use(
  (config) => {
    // Skip auth for public endpoints
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/products',
      '/categories',
      '/promotions/active'
    ];
    
    const isPublic = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    if (!isPublic) {
      const token = localStorage.getItem('token') || localStorage.getItem('userToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration for users
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh-token', { refreshToken });
          const { token } = response.data.data;
          
          localStorage.setItem('token', token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// ADMIN response interceptor
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Helper function to extract data from response
const extractData = (response) => {
  try {
    const responseData = response.data;
    
    if (responseData && responseData.success !== undefined) {
      return responseData.data || [];
    }
    
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    if (responseData && responseData.data !== undefined) {
      return responseData.data || [];
    }
    
    console.warn('Unexpected API response format:', responseData);
    return [];
  } catch (error) {
    console.error('Error extracting data from response:', error);
    return [];
  }
};

// ==================== AUTH APIs (Customer & Admin) ====================
export const authAPI = {
  // Customer Registration
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.success) {
      const { token, refreshToken, user } = response.data.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }
    return response.data;
  },
  
  // Customer Login
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.success) {
      const { token, refreshToken, user } = response.data.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }
    return response.data;
  },
  
  // Admin Login
  adminLogin: async (credentials) => {
    const response = await api.post('/admin/auth/login', credentials);
    if (response.data.success) {
      const { token, user } = response.data.data;
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
    }
    return response.data;
  },
  
  // Logout (Customer)
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },
  
  // Logout (Admin)
  adminLogout: async () => {
    try {
      await adminApi.post('/admin/auth/logout');
    } catch (error) {
      console.error('Admin logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  },
  
  // Get current user profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
  
  // Update profile
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    if (response.data.success) {
      localStorage.setItem('user', JSON.stringify(response.data.data));
    }
    return response.data;
  },
  
  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },
  
  // Refresh token
  refreshToken: async (refreshToken) => {
    const response = await api.post('/auth/refresh-token', { refreshToken });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
    }
    return response.data;
  }
};

// ==================== CART APIs ====================
export const cartAPI = {
  getCart: async () => {
    const response = await api.get('/cart');
    return response.data;
  },
  
  addToCart: async (productData) => {
    const response = await api.post('/cart/add', productData);
    return response.data;
  },
  
  updateCartItem: async (itemId, quantity) => {
    const response = await api.put(`/cart/update/${itemId}`, { quantity });
    return response.data;
  },
  
  removeFromCart: async (itemId) => {
    const response = await api.delete(`/cart/remove/${itemId}`);
    return response.data;
  },
  
  getCartCount: async () => {
    const response = await api.get('/cart/count');
    return response.data;
  },
  
  clearCart: async () => {
    const response = await api.delete('/cart/clear');
    return response.data;
  },
  
  reserveStock: async () => {
    const response = await api.post('/cart/reserve-stock');
    return response.data;
  }
};

// ==================== ORDER & PAYMENT APIs ====================
export const orderAPI = {
  // User orders
  getUserOrders: async (params) => {
    const response = await api.get('/orders/user', { params });
    return response.data;
  },
  
  getOrder: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },
  
  cancelOrder: async (id) => {
    const response = await api.put(`/orders/${id}/cancel`);
    return response.data;
  },
  
  // Payment
  initializePayment: async (paymentData) => {
    const response = await api.post('/paystack/initialize', paymentData);
    return response.data;
  },
  
  verifyPayment: async (reference) => {
    const response = await api.get(`/paystack/verify/${reference}`);
    return response.data;
  }
};

// ==================== PRODUCT APIs ====================
export const productAPI = {
  // Customer endpoints
  getAll: async (params) => {
    const response = await api.get('/products', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  
  search: async (query) => {
    const response = await api.get(`/products?search=${query}`);
    return response.data;
  },
  
  getByCategory: async (category) => {
    const response = await api.get(`/products?category=${category}`);
    return response.data;
  },
  
  // Admin endpoints
  admin: {
    getAll: async (params) => {
      const response = await adminApi.get('/admin/products', { params });
      return response.data;
    },
    
    getById: async (id) => {
      const response = await adminApi.get(`/admin/products/${id}`);
      return response.data;
    },
    
    create: async (data) => {
      const response = await adminApi.post('/admin/products', data);
      return response.data;
    },
    
    update: async (id, data) => {
      const response = await adminApi.put(`/admin/products/${id}`, data);
      return response.data;
    },
    
    delete: async (id) => {
      const response = await adminApi.delete(`/admin/products/${id}`);
      return response.data;
    }
  }
};

// ==================== CATEGORY APIs ====================
// ==================== CATEGORY APIs ====================
export const categoryAPI = {
  // Customer endpoints
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  },
  
  getMain: async () => {
    const response = await api.get('/categories/main');
    return response.data?.data || [];
  },

  getSubcategories: async (id) => {
    const response = await api.get(`/categories/subcategories/${id}`);
    return response.data?.data || [];
  },

  getHierarchy: async () => {
    const response = await api.get('/categories/hierarchy');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data?.data || response.data;
  },
  
  getByLevel: async (level) => {
    const response = await api.get(`/categories/level/${level}`);
    return response.data;
  },
  
  // ===== USE EXISTING ENDPOINTS =====
  // Use the existing POST /api/categories endpoint
  create: async (data) => {
    const response = await api.post('/categories', data);
    return response.data;
  },
  
  // Use the existing PUT /api/categories/:id endpoint 
  // (You'll need to add this endpoint to your routes)
  update: async (id, data) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },
  
  // Use the existing DELETE /api/categories/:id endpoint
  // (You'll need to add this endpoint to your routes)
  delete: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
  
  adminGetAll: async (params) => {
    // Use the existing GET /api/categories endpoint for now
    const response = await api.get('/categories', { params });
    return response.data;
  }
};
// ==================== PROMOTION APIs ====================
export const promotionAPI = {
  // Get active promotions (uses your existing axios instance)
  getActive: async (position) => {
    try {
      const params = position ? { position } : {};
      const response = await api.get('/promotions/active', { params });
      console.log('🎯 Promotions API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching promotions:', error);
      // Return empty structure for graceful fallback
      return {
        success: false,
        data: [],
        message: error.message
      };
    }
  },
  
  // Get all promotions (admin)
  adminGetAll: async (params) => {
    const response = await adminApi.get('/admin/promotions', { params });
    return response.data;
  },
  
  // Get promotion by ID
  getById: async (id) => {
    const response = await api.get(`/promotions/${id}`);
    return response.data;
  },
  
  // Track click (using axios, not fetch)
  trackClick: async (id) => {
    const response = await api.post(`/promotions/${id}/track-click`);
    return response.data;
  }
};
// ==================== UPLOAD APIs ====================
export const uploadAPI = {
  uploadSingle: async (formData) => {
    const response = await api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  uploadMultiple: async (formData) => {
    const response = await api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// ==================== ADMIN USER MANAGEMENT ====================
export const adminUserAPI = {
  getAll: async (params) => {
    const response = await adminApi.get('/admin/users', { params });
    return response.data;
  },
  
  updateStatus: async (id, data) => {
    const response = await adminApi.put(`/admin/users/${id}/status`, data);
    return response.data;
  }
};

// ==================== EXPORT DEFAULT ====================
export default api;
export { adminApi };